// Builds the per-message UI snapshot David receives (via forwardedProps). Read
// at send time from useAppStore.getState() — no subscription, no re-render.
//
// Shape matches the backend model (hoku/ui_context.py); the FE sends camelCase
// keys (currentHref/activeTabId) which the backend aliases. The WIRE IS FROZEN
// at v1: tab `id` is the CANONICAL tab pathname ('/duties/abc' even when the
// href sits on a sub-view), while `href` carries the visited sub-path plus the
// task query. The store keys tabs by manifest id instead, so this file adapts
// between the two — changing the wire shape needs a backend change first.

import { type EntityRef, parseEntityFromUrl } from '@/config/entityRoutes'
import { matchAppRoute, resolveTabLocation } from '@/config/routeManifest'
import { pathnameOf } from '@/lib/shellSearch'
import { useAppStore, type WorkspaceTab } from '@/stores/app'

const TITLE_MAX = 80
const TABS_MAX = 25
const HREF_MAX = 1024

// hoku/ui_context.py declares href and currentHref as max_length=1024, and the
// parse is ALL-OR-NOTHING: one over-long href drops the entire snapshot, so
// David loses every tab. A filter-heavy table URL is the realistic way to get
// there, so drop the query and keep the addressable part.
function clampHref(href: string): string {
  if (href.length <= HREF_MAX) return href
  const hashIndex = href.indexOf('#')
  const hash = hashIndex === -1 ? '' : href.slice(hashIndex)
  return (pathnameOf(href) + hash).slice(0, HREF_MAX)
}

export interface UiTabSnapshot {
  id: string
  title: string
  href: string
  active: boolean
  entity?: EntityRef
}

export interface UiContextSnapshot {
  version: 1
  currentHref: string
  currentEntity?: EntityRef
  activeTabId: string | null
  tabs: UiTabSnapshot[]
}

// v1 wire id: the canonical tab pathname (entry.tab mapping when present),
// so a duty tab visited at /duties/abc/call still reports id '/duties/abc'.
function wireIdOf(tab: WorkspaceTab): string {
  const pathname = pathnameOf(tab.href)
  const matched = matchAppRoute(pathname)
  return matched?.route.tab?.(matched.params) ?? pathname
}

function toTabSnapshot(
  tab: WorkspaceTab,
  activeTabId: string | null
): UiTabSnapshot {
  const entity = parseEntityFromUrl(tab.href)
  return {
    id: wireIdOf(tab),
    title: tab.title.slice(0, TITLE_MAX),
    href: clampHref(tab.href),
    active: activeTabId !== null && tab.id === activeTabId,
    ...(entity ? { entity } : {}),
  }
}

// Cap to TABS_MAX, always keeping the active tab (the backend also caps, but the
// FE decides *which* tabs survive so the active one is never dropped).
function capKeepingActive(tabs: UiTabSnapshot[]): UiTabSnapshot[] {
  if (tabs.length <= TABS_MAX) return tabs
  const kept = tabs.slice(0, TABS_MAX)
  if (kept.some((t) => t.active)) return kept
  const active = tabs.find((t) => t.active)
  return active ? [...kept.slice(0, TABS_MAX - 1), active] : kept
}

export function buildUiContextSnapshot(input: {
  tabs: WorkspaceTab[]
  /**
   * Store-shaped active tab id (tabIdFor, derived from the URL by the caller).
   * Distinct from the snapshot's `activeTabId`, which is the v1 wire id.
   */
  activeStoreTabId: string | null
  currentHref: string
}): UiContextSnapshot {
  const currentEntity = parseEntityFromUrl(input.currentHref)
  const snapshots = capKeepingActive(
    input.tabs.map((t) => toTabSnapshot(t, input.activeStoreTabId))
  )
  return {
    version: 1,
    currentHref: clampHref(input.currentHref),
    ...(currentEntity ? { currentEntity } : {}),
    // v1 wire: the URL-shaped id of the active tab, not the store id.
    activeTabId: snapshots.find((t) => t.active)?.id ?? null,
    tabs: snapshots,
  }
}

export function getUiContextSnapshot(): UiContextSnapshot {
  const { items } = useAppStore.getState().tabs
  const { pathname, search, hash } = window.location
  return buildUiContextSnapshot({
    tabs: items,
    activeStoreTabId:
      resolveTabLocation({ pathname, search, hash })?.id ?? null,
    currentHref: pathname + search + hash,
  })
}
